import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signOut,
  setPersistence,
  inMemoryPersistence,
  deleteUser,
} from 'firebase/auth';
import {
  getDatabase,
  ref,
  onValue,
  onChildAdded,
  update,
  push,
  get,
  child,
} from 'firebase/database';
import {
  all,
  takeLatest,
  takeLeading,
  takeEvery,
  put,
  call,
  select,
  take,
} from 'redux-saga/effects';
import { eventChannel, buffers } from 'redux-saga';
import axios from 'axios';
import { message } from 'antd';
import { notification } from '../../components';
import { firebaseConfig } from '../../settings';
import actions from './actions';
import appActions from '../app/actions';
import {
  DEFAULT_STAGE, randomAvatarColor, buildWelcomeEmail, buildMentionEmail,
} from '../../components/postSale/constants';

const ROOT_URL = process.env.REACT_APP_CLOUD_FUNCTIONS_ROOT_URL;

// Caminho global (admin-only): o kanban de pós-vendas é interno, não pertence
// a um médico/endereço como o de orçamentos.
const CARDS_PATH = 'postSale/cards';
const LOGS_PATH = 'postSale/logs';
const MENTIONS_PATH = 'postSale/userMentions';
const COMMENTS_PATH = 'postSale/comments';

const getCardsFromStore = (state) => state.PostSale.cards;

function sanitizeForFirebase(obj) {
  if (obj === null || obj === undefined) return null;
  if (Array.isArray(obj)) return obj.map(sanitizeForFirebase);
  if (typeof obj === 'object') {
    const cleaned = {};
    Object.keys(obj).forEach((key) => {
      const val = obj[key];
      cleaned[key] = val === undefined ? null : sanitizeForFirebase(val);
    });
    return cleaned;
  }
  return obj;
}

// --------------- Activity Log Helper ---------------

function* writePostSaleLog(logData) {
  try {
    const db = getDatabase();
    const logKey = push(child(ref(db), LOGS_PATH)).key;
    const actorUid = getAuth().currentUser?.uid || null;
    const logEntry = {
      id: logKey,
      actorUid,
      ...logData,
      createdAt: Date.now(),
    };
    yield call(() => update(ref(db), { [`${LOGS_PATH}/${logKey}`]: logEntry }));
  } catch (error) {
    // Falha de log não deve bloquear a operação principal
    console.warn('Failed to write post-sale log:', error);
  }
}

// --------------- Real-time listener ---------------

function createCardsListener() {
  const db = getDatabase();
  return eventChannel((emit) => {
    const cardsRef = ref(db, CARDS_PATH);
    const unsubscribe = onValue(
      cardsRef,
      (snapshot) => {
        const val = snapshot.val();
        if (val) {
          const list = Object.keys(val)
            .map((key) => ({ ...val[key], id: key }))
            .filter((c) => !c.deletedAt);
          emit(list);
        } else {
          emit([]);
        }
      },
      () => emit([]),
    );
    return () => unsubscribe();
  }, buffers.expanding(5));
}

export function* fetchCardsRequest() {
  let currentListener = null;
  yield takeLatest(actions.FETCH_CARDS_REQUEST, function* () {
    try {
      if (currentListener) {
        currentListener.close();
        currentListener = null;
      }
      yield put({ type: actions.FETCHING_CARDS });
      currentListener = yield call(createCardsListener);
      yield takeEvery(currentListener, function* (cards) {
        yield put({ type: actions.FETCH_CARDS_SUCCESS, payload: cards });
      });
      yield take(appActions.CANCEL_LISTENERS);
      if (currentListener) {
        currentListener.close();
        currentListener = null;
      }
    } catch (error) {
      yield put({ type: actions.FETCH_CARDS_ERROR });
      notification('error', 'Erro ao buscar cards de pós-vendas', error.message);
    }
  });
}

// --------------- Save card (create / update) ---------------

function* saveCardOnDB(action) {
  try {
    yield put({ type: actions.SAVING_CARD });
    const card = action.payload;
    const db = getDatabase();
    const now = Date.now();
    const actorUid = getAuth().currentUser?.uid || null;

    if (card.id) {
      // Edição: merge por campo, preservando dados de etapas futuras
      // (boas-vindas, conta vinculada, métricas etc.).
      const { id, ...rest } = card;
      const updates = {};
      Object.keys(rest).forEach((key) => {
        updates[`${CARDS_PATH}/${id}/${key}`] = sanitizeForFirebase(rest[key]);
      });
      updates[`${CARDS_PATH}/${id}/updatedAt`] = now;
      yield call(() => update(ref(db), updates));
      yield call(writePostSaleLog, {
        cardId: id,
        action: 'edited',
        details: { clinicName: rest.clinicName || null },
      });
    } else {
      const newKey = push(child(ref(db), CARDS_PATH)).key;
      const newCard = sanitizeForFirebase({
        ...card,
        id: newKey,
        stage: card.stage || DEFAULT_STAGE,
        source: card.source || 'manual',
        avatarColor: card.avatarColor || randomAvatarColor(),
        createdBy: actorUid,
        createdAt: now,
        updatedAt: now,
      });
      yield call(() => update(ref(db), { [`${CARDS_PATH}/${newKey}`]: newCard }));
      yield call(writePostSaleLog, {
        cardId: newKey,
        action: 'created',
        details: { clinicName: newCard.clinicName || null, stage: newCard.stage },
      });
    }

    yield put({ type: actions.SAVE_CARD_SUCCESS });
    message.success('Salvo');
  } catch (error) {
    yield put({ type: actions.SAVE_CARD_ERROR });
    message.error('Erro ao salvar');
  }
}

export function* saveCardRequest() {
  yield takeLatest(actions.SAVE_CARD_REQUEST, saveCardOnDB);
}

// --------------- Update stage (drag entre colunas) ---------------

function* updateCardStageOnDB(action) {
  try {
    const { cardId, stage } = action.payload;
    const db = getDatabase();
    const cards = yield select(getCardsFromStore);
    const current = cards.find((c) => c.id === cardId);
    const previousStage = current ? current.stage : null;
    const updates = {};
    updates[`${CARDS_PATH}/${cardId}/stage`] = stage;
    updates[`${CARDS_PATH}/${cardId}/updatedAt`] = Date.now();
    yield call(() => update(ref(db), updates));
    yield call(writePostSaleLog, {
      cardId,
      action: 'stage_changed',
      details: { from: previousStage, to: stage },
    });
    yield put({ type: actions.UPDATE_CARD_STAGE_SUCCESS });
  } catch (error) {
    yield put({ type: actions.UPDATE_CARD_STAGE_ERROR });
    message.error('Erro ao mover card');
  }
}

export function* updateCardStageRequest() {
  yield takeLatest(actions.UPDATE_CARD_STAGE_REQUEST, updateCardStageOnDB);
}

// --------------- Update migration stage (board de migração) ---------------

function* updateMigrationStageOnDB(action) {
  try {
    const { cardId, migrationStage } = action.payload;
    const db = getDatabase();
    const cards = yield select(getCardsFromStore);
    const current = cards.find((c) => c.id === cardId);
    const previousStage = current ? current.migrationStage : null;
    const updates = {};
    updates[`${CARDS_PATH}/${cardId}/migrationStage`] = migrationStage;
    updates[`${CARDS_PATH}/${cardId}/updatedAt`] = Date.now();
    yield call(() => update(ref(db), updates));
    yield call(writePostSaleLog, {
      cardId,
      action: 'migration_stage_changed',
      details: { from: previousStage, to: migrationStage },
    });
    yield put({ type: actions.UPDATE_MIGRATION_STAGE_SUCCESS });
  } catch (error) {
    yield put({ type: actions.UPDATE_MIGRATION_STAGE_ERROR });
    message.error('Erro ao mover card de migração');
  }
}

export function* updateMigrationStageRequest() {
  yield takeLatest(actions.UPDATE_MIGRATION_STAGE_REQUEST, updateMigrationStageOnDB);
}

// --------------- Reorder cards (sortOrder) ---------------

function* reorderCardsOnDB(action) {
  try {
    const { orderedIds, field = 'sortOrder' } = action.payload; // [{ id, sortOrder }]
    const db = getDatabase();
    const updates = {};
    orderedIds.forEach(({ id, sortOrder }) => {
      updates[`${CARDS_PATH}/${id}/${field}`] = sortOrder;
    });
    yield call(() => update(ref(db), updates));
    yield put({ type: actions.REORDER_CARDS_SUCCESS });
  } catch (error) {
    yield put({ type: actions.REORDER_CARDS_ERROR });
  }
}

export function* reorderCardsRequest() {
  yield takeLatest(actions.REORDER_CARDS_REQUEST, reorderCardsOnDB);
}

// --------------- Delete card (soft delete) ---------------

function* deleteCardOnDB(action) {
  try {
    const cardId = action.payload;
    const db = getDatabase();
    const cards = yield select(getCardsFromStore);
    const deleted = cards.find((c) => c.id === cardId);
    const updates = {};
    updates[`${CARDS_PATH}/${cardId}/deletedAt`] = Date.now();
    yield call(() => update(ref(db), updates));
    yield call(writePostSaleLog, {
      cardId,
      action: 'deleted',
      details: { clinicName: deleted ? deleted.clinicName || null : null },
    });
    yield put({ type: actions.DELETE_CARD_SUCCESS });
    message.success('Card excluído');
  } catch (error) {
    yield put({ type: actions.DELETE_CARD_ERROR });
    message.error('Erro ao excluir');
  }
}

export function* deleteCardRequest() {
  yield takeLatest(actions.DELETE_CARD_REQUEST, deleteCardOnDB);
}

// --------------- Registrar boas-vindas enviadas ---------------

function* markWelcomeSentOnDB(action) {
  try {
    const { cardId, channel } = action.payload;
    const db = getDatabase();
    const now = Date.now();
    const field = channel === 'email' ? 'welcomeSentEmail' : 'welcomeSentWhatsApp';
    const updates = {};
    updates[`${CARDS_PATH}/${cardId}/${field}`] = true;
    updates[`${CARDS_PATH}/${cardId}/welcomeSentAt`] = now;
    updates[`${CARDS_PATH}/${cardId}/updatedAt`] = now;
    yield call(() => update(ref(db), updates));
    yield call(writePostSaleLog, {
      cardId,
      action: 'welcome_sent',
      details: { channel },
    });
  } catch (error) {
    message.error('Erro ao registrar boas-vindas');
  }
}

export function* markWelcomeSentRequest() {
  yield takeEvery(actions.MARK_WELCOME_SENT_REQUEST, markWelcomeSentOnDB);
}

// --------------- Enviar boas-vindas por e-mail (HTML via sendGenericEmail) ---------------

function* sendWelcomeEmailToClient(action) {
  try {
    yield put({ type: actions.SENDING_WELCOME_EMAIL });
    const card = action.payload;
    const to = card.accountEmail || card.email;
    if (!to) {
      message.error('Card sem e-mail para envio');
      yield put({ type: actions.SEND_WELCOME_EMAIL_ERROR });
      return;
    }

    const { subject, text, html } = buildWelcomeEmail(card);
    yield call(() => axios.post(`${ROOT_URL}/sendGenericEmail`, {
      to, subject, text, html,
    }));

    const db = getDatabase();
    const now = Date.now();
    const updates = {};
    updates[`${CARDS_PATH}/${card.id}/welcomeSentEmail`] = true;
    updates[`${CARDS_PATH}/${card.id}/welcomeSentAt`] = now;
    updates[`${CARDS_PATH}/${card.id}/updatedAt`] = now;
    yield call(() => update(ref(db), updates));
    yield call(writePostSaleLog, {
      cardId: card.id,
      action: 'welcome_sent',
      details: { channel: 'email' },
    });

    yield put({ type: actions.SEND_WELCOME_EMAIL_SUCCESS });
    message.success('E-mail de boas-vindas enviado');
  } catch (error) {
    yield put({ type: actions.SEND_WELCOME_EMAIL_ERROR });
    message.error('Erro ao enviar e-mail de boas-vindas');
  }
}

export function* sendWelcomeEmailRequest() {
  yield takeLeading(actions.SEND_WELCOME_EMAIL_REQUEST, sendWelcomeEmailToClient);
}

// --------------- Comentários ---------------

function* fetchCommentsFromDB(action) {
  try {
    yield put({ type: actions.FETCHING_COMMENTS });
    const cardId = action.payload;
    const db = getDatabase();
    const snapshot = yield call(() => get(ref(db, `${COMMENTS_PATH}/${cardId}`)));
    const val = snapshot.val();
    let comments = [];
    if (val) {
      comments = Object.keys(val)
        .map((key) => ({ ...val[key], id: key }))
        .filter((c) => !c.deletedAt)
        .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    }
    yield put({ type: actions.FETCH_COMMENTS_SUCCESS, payload: comments });
    // Mantém um contador denormalizado no card para exibir no kanban (sem precisar
    // ler os comentários de todos os cards). Backfilla cards antigos ao abrir.
    yield call(() => update(ref(db), {
      [`${CARDS_PATH}/${cardId}/commentCount`]: comments.length,
    }));
  } catch (error) {
    yield put({ type: actions.FETCH_COMMENTS_ERROR });
  }
}

export function* fetchCommentsRequest() {
  yield takeLatest(actions.FETCH_COMMENTS_REQUEST, fetchCommentsFromDB);
}

// Envia e-mail de menção (não bloqueia o fluxo principal se falhar).
function* sendMentionEmail(target, comment) {
  if (!target || !target.email) return;
  try {
    const { subject, text, html } = buildMentionEmail({
      recipientName: target.name,
      authorName: comment.authorName,
      clinicName: comment.clinicName,
      snippet: (comment.text || '').slice(0, 200),
    });
    yield call(() => axios.post(`${ROOT_URL}/sendGenericEmail`, {
      to: target.email, subject, text, html,
    }));
  } catch (error) {
    console.warn('[postSale] falha ao enviar e-mail de menção', error);
  }
}

function* saveCommentOnDB(action) {
  try {
    yield put({ type: actions.SAVING_COMMENT });
    const comment = action.payload;
    const db = getDatabase();
    const now = Date.now();
    const currentUserId = getAuth().currentUser?.uid || null;

    // mentions = [{ userId, name, email }] vindos do tab (customUsers).
    const mentions = Array.isArray(comment.mentions) ? comment.mentions : [];
    const newMentioned = mentions.map((m) => m.userId).filter(Boolean);

    const updates = {};
    let commentKey;
    let oldMentioned = [];

    if (comment.id) {
      commentKey = comment.id;
      const oldSnap = yield call(() => get(ref(db, `${COMMENTS_PATH}/${comment.cardId}/${comment.id}`)));
      oldMentioned = (oldSnap.val() && oldSnap.val().mentionedUserIds) || [];
      updates[`${COMMENTS_PATH}/${comment.cardId}/${comment.id}/text`] = comment.text;
      updates[`${COMMENTS_PATH}/${comment.cardId}/${comment.id}/mentionedUserIds`] = newMentioned;
      updates[`${COMMENTS_PATH}/${comment.cardId}/${comment.id}/updatedAt`] = now;
    } else {
      commentKey = push(child(ref(db), `${COMMENTS_PATH}/${comment.cardId}`)).key;
      updates[`${COMMENTS_PATH}/${comment.cardId}/${commentKey}`] = {
        id: commentKey,
        cardId: comment.cardId,
        text: comment.text,
        authorName: comment.authorName || '',
        authorUid: currentUserId,
        createdAt: now,
        mentionedUserIds: newMentioned,
      };
    }

    // Fan-out global em postSale/userMentions/<targetId>/<commentId> (não menciona a si).
    const removedTargets = oldMentioned.filter((id) => !newMentioned.includes(id));
    const addedTargets = newMentioned.filter((id) => !oldMentioned.includes(id) && id !== currentUserId);

    removedTargets.forEach((targetId) => {
      updates[`${MENTIONS_PATH}/${targetId}/${commentKey}`] = null;
    });
    addedTargets.forEach((targetId) => {
      updates[`${MENTIONS_PATH}/${targetId}/${commentKey}`] = {
        commentId: commentKey,
        cardId: comment.cardId,
        clinicName: comment.clinicName || '',
        authorName: comment.authorName || '',
        authorUid: currentUserId,
        snippet: (comment.text || '').slice(0, 120),
        createdAt: now,
      };
    });

    yield call(() => update(ref(db), updates));
    yield put({ type: actions.SAVE_COMMENT_SUCCESS });
    yield call(fetchCommentsFromDB, { payload: comment.cardId });

    // E-mail para cada novo mencionado (exceto o próprio autor).
    const addedMentions = mentions.filter((m) => addedTargets.includes(m.userId));
    for (let i = 0; i < addedMentions.length; i += 1) {
      yield call(sendMentionEmail, addedMentions[i], comment);
    }
  } catch (error) {
    yield put({ type: actions.SAVE_COMMENT_ERROR });
    message.error('Erro ao salvar comentário');
  }
}

export function* saveCommentRequest() {
  yield takeLatest(actions.SAVE_COMMENT_REQUEST, saveCommentOnDB);
}

function* deleteCommentOnDB(action) {
  try {
    const { commentId, cardId } = action.payload;
    const db = getDatabase();
    yield call(() => update(ref(db), {
      [`${COMMENTS_PATH}/${cardId}/${commentId}/deletedAt`]: Date.now(),
    }));
    yield call(fetchCommentsFromDB, { payload: cardId });
  } catch (error) {
    message.error('Erro ao excluir comentário');
  }
}

export function* deleteCommentRequest() {
  yield takeLatest(actions.DELETE_COMMENT_REQUEST, deleteCommentOnDB);
}

// --------------- Menções (notificação no topbar) ---------------

function createMentionsListener(currentUserId) {
  const db = getDatabase();
  const mentionsRef = ref(db, `${MENTIONS_PATH}/${currentUserId}`);
  return eventChannel((emit) => {
    const unsubscribe = onChildAdded(
      mentionsRef,
      (snapshot) => emit({ id: snapshot.key, ...snapshot.val() }),
      (error) => console.warn('[postSale mentions] subscribe error', error),
    );
    return () => unsubscribe();
  }, buffers.expanding(10));
}

export function* mentionsListenerRequest() {
  let channel = null;
  yield takeLatest(actions.START_MENTIONS_LISTENER, function* () {
    try {
      if (channel) { channel.close(); channel = null; }
      const currentUserId = getAuth().currentUser?.uid;
      if (!currentUserId) return;
      channel = yield call(createMentionsListener, currentUserId);
      const subscribeTime = Date.now();
      yield takeEvery(channel, function* (mention) {
        // isNew distingue escrita fresca (vale toast) de replay de linhas antigas.
        const isNew = (mention.createdAt || 0) > (subscribeTime - 2000);
        yield put({ type: actions.MENTION_RECEIVED, payload: { ...mention, isNew } });
      });
      yield take(appActions.CANCEL_LISTENERS);
      if (channel) { channel.close(); channel = null; }
    } catch (error) {
      console.warn('[postSale mentions] listener error', error);
    }
  });
}

function* dismissMentionOnDB(action) {
  try {
    const mentionId = action.payload;
    const currentUserId = getAuth().currentUser?.uid;
    if (!currentUserId || !mentionId) return;
    const db = getDatabase();
    yield call(() => update(ref(db), {
      [`${MENTIONS_PATH}/${currentUserId}/${mentionId}`]: null,
    }));
  } catch (error) {
    console.warn('[postSale mentions] dismiss error', error);
  }
}

export function* dismissMentionRequest() {
  yield takeEvery(actions.DISMISS_MENTION_REQUEST, dismissMentionOnDB);
}

// --------------- Activity logs ---------------

function* fetchLogsFromDB(action) {
  try {
    yield put({ type: actions.FETCHING_LOGS });
    const cardId = action.payload;
    const db = getDatabase();
    const snapshot = yield call(() => get(ref(db, LOGS_PATH)));
    const val = snapshot.val();
    let logs = [];
    if (val) {
      logs = Object.keys(val)
        .map((key) => ({ ...val[key], id: key }))
        .filter((log) => !cardId || log.cardId === cardId)
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    }
    yield put({ type: actions.FETCH_LOGS_SUCCESS, payload: logs });
  } catch (error) {
    yield put({ type: actions.FETCH_LOGS_ERROR });
  }
}

export function* fetchLogsRequest() {
  yield takeLatest(actions.FETCH_LOGS_REQUEST, fetchLogsFromDB);
}

// --------------- Criar conta do cliente (Boas-vindas) ---------------

// App secundária com persistência em memória: o createUserWithEmailAndPassword loga
// o usuário recém-criado no auth que o executa. Isolando numa app à parte (e sem
// persistir em disco), a sessão do admin (Flávia) permanece intacta.
const SECONDARY_APP_NAME = 'postsale-provisioning';

function getSecondaryApp() {
  const existing = getApps().find((a) => a.name === SECONDARY_APP_NAME);
  return existing || initializeApp(firebaseConfig, SECONDARY_APP_NAME);
}

function createAccountFriendlyError(error) {
  switch (error.code) {
    case 'auth/email-already-in-use':
      return 'Este e-mail já tem conta no Meagenda.';
    case 'auth/invalid-email':
      return 'E-mail inválido.';
    case 'auth/weak-password':
      return 'Senha muito fraca (mínimo 6 caracteres).';
    default:
      return error.message;
  }
}

function* createAccountForCard(action) {
  const secondaryApp = getSecondaryApp();
  const secondaryAuth = getAuth(secondaryApp);
  try {
    yield put({ type: actions.CREATING_ACCOUNT });
    const {
      cardId, firstName, lastName, email, password, maxCustomHealthProfessionals,
    } = action.payload;

    // Nada da sessão secundária é persistido em disco.
    yield call(() => setPersistence(secondaryAuth, inMemoryPersistence));
    const cred = yield call(() => createUserWithEmailAndPassword(secondaryAuth, email, password));
    const newUid = cred.user.uid;
    const idToken = yield call(() => cred.user.getIdToken());

    // Cria o professional no Firestore + claim professional:true (mesmo endpoint do signup).
    try {
      yield call(() => axios.post(
        `${ROOT_URL}/createProfessional`,
        {
          firstName,
          lastName,
          email,
          uid: newUid,
          maxCustomHealthProfessionals: maxCustomHealthProfessionals || 0,
        },
        { headers: { Authorization: `Bearer ${idToken}` } },
      ));
    } catch (profError) {
      // Rollback: o usuário Auth foi criado mas o professional falhou. Apaga o usuário
      // recém-criado para não deixar órfão (senão o retry acusa "e-mail já em uso").
      try {
        yield call(() => deleteUser(cred.user));
      } catch (delErr) {
        console.warn('[postSale] rollback do usuário Auth falhou', delErr);
      }
      throw profError;
    }

    yield call(() => signOut(secondaryAuth));

    const db = getDatabase();
    const now = Date.now();
    const contactName = `${firstName} ${lastName}`.trim();
    const updates = {};
    updates[`${CARDS_PATH}/${cardId}/linkedAccountUid`] = newUid;
    updates[`${CARDS_PATH}/${cardId}/accountEmail`] = email;
    updates[`${CARDS_PATH}/${cardId}/tempPassword`] = password;
    updates[`${CARDS_PATH}/${cardId}/accountCreatedAt`] = now;
    // Reflete no objeto principal o que foi (eventualmente) editado na tela de criar conta.
    updates[`${CARDS_PATH}/${cardId}/email`] = email;
    if (contactName) updates[`${CARDS_PATH}/${cardId}/contactName`] = contactName;
    updates[`${CARDS_PATH}/${cardId}/updatedAt`] = now;
    yield call(() => update(ref(db), updates));
    yield call(writePostSaleLog, {
      cardId,
      action: 'account_created',
      details: { uid: newUid, email },
    });

    yield put({ type: actions.CREATE_ACCOUNT_SUCCESS, payload: { cardId, uid: newUid } });
    notification('success', 'Conta criada com sucesso!', `Login: ${email}`);
  } catch (error) {
    // Encerra a sessão secundária mesmo em erro, para não deixar resíduo.
    try {
      yield call(() => signOut(secondaryAuth));
    } catch (signOutErr) {
      console.warn('[postSale] signOut secundário falhou', signOutErr);
    }
    yield put({ type: actions.CREATE_ACCOUNT_ERROR });
    notification('error', 'Erro ao criar conta', createAccountFriendlyError(error));
  }
}

export function* createAccountRequest() {
  yield takeLeading(actions.CREATE_ACCOUNT_REQUEST, createAccountForCard);
}

export default function* postSaleSaga() {
  yield all([
    fetchCardsRequest(),
    saveCardRequest(),
    updateCardStageRequest(),
    updateMigrationStageRequest(),
    reorderCardsRequest(),
    deleteCardRequest(),
    fetchLogsRequest(),
    createAccountRequest(),
    markWelcomeSentRequest(),
    sendWelcomeEmailRequest(),
    fetchCommentsRequest(),
    saveCommentRequest(),
    deleteCommentRequest(),
    mentionsListenerRequest(),
    dismissMentionRequest(),
  ]);
}
